import { MemberRole } from '@/services/internal-api/accounts/types/accounts-api-service-enums';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import InvitationForm from './invitation-form';
import InviteMemberDialogTabs from './invite-member-dialog-tabs';
import { type TeamMember, InviteFormValues } from '../types/team-types';
import { inviteFormSchema } from '../constants/invitation-schema';

import { BUTTON_SIZE, BUTTON_VARIANT } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { DIALOG_DISMISSAL, DIALOG_SIZE } from '@/components/ui/types/dialog-variants-enums';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSubmit: (data: InviteFormValues) => void;
  isSubmitting?: boolean;
  currentUserRole: TeamMember['role'] | null;
  showPendingMembers?: boolean;
  teamMembers?: TeamMember[];
  // Team management functions and state (required when showPendingMembers is true)
  onRoleChange?: (memberId: string | number, newRole: TeamMember['role']) => void;
  onResendInvite?: (memberId: string | number) => void;
  onCancelInvite?: (memberId: string | number) => void;
  onDeleteMember?: (memberId: string | number) => void;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  mutationErrorMessage?: string;
}

const EMPTY_TEAM_MEMBERS: TeamMember[] = [];

export default function InviteMemberDialog({
  open,
  onOpenChange,
  onInviteSubmit,
  isSubmitting = false,
  currentUserRole,
  showPendingMembers = false,
  teamMembers = EMPTY_TEAM_MEMBERS,
  onRoleChange,
  onResendInvite,
  onCancelInvite,
  onDeleteMember,
  isLoading = false,
  isError = false,
  error = null,
  mutationErrorMessage,
}: InviteMemberDialogProps) {
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      invitations: [{ email: '', role: MemberRole.MEMBER }],
    },
  });
  const invitations = useWatch({ control: form.control, name: 'invitations' });
  const invitationCount = invitations.length;
  const isBulkInvite = invitationCount > 1;
  const isInviteSubmitting = form.formState.isSubmitting || isSubmitting;
  const submitLabel = isBulkInvite ? 'Send invitations' : 'Send invitation';
  const loadingLabel = isBulkInvite ? 'Sending invitations…' : 'Sending invitation…';

  useEffect(() => {
    form.reset({
      invitations: [{ email: '', role: MemberRole.MEMBER }],
      message: '',
    });
  }, [open, form]);

  const onSubmit = (data: InviteFormValues) => {
    onInviteSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        busy={isInviteSubmitting}
        className={cn('sm:max-w-[600px]', showPendingMembers && 'sm:max-w-[1000px]')}
        dismissal={DIALOG_DISMISSAL.STANDARD}
        size={showPendingMembers ? DIALOG_SIZE.FULL : DIALOG_SIZE.MD}
      >
        <DialogHeader>
          <DialogTitle className="type-dialog-title">Invite member</DialogTitle>
          <DialogDescription>
            Enter an email address and choose a role. Invitations expire after 5 days.
          </DialogDescription>
        </DialogHeader>

        {showPendingMembers ? (
          <InviteMemberDialogTabs
            form={form}
            onSubmit={onSubmit}
            currentUserRole={currentUserRole}
            teamMembers={teamMembers}
            onRoleChange={onRoleChange || (() => {})}
            onResendInvite={onResendInvite || (() => {})}
            onCancelInvite={onCancelInvite || (() => {})}
            onDeleteMember={onDeleteMember || (() => {})}
            isLoading={isLoading}
            isError={isError}
            error={error}
          />
        ) : (
          <InvitationForm
            form={form}
            onSubmit={onSubmit}
            currentUserRole={currentUserRole}
            className="mb-4 space-y-4 py-3"
          />
        )}

        {mutationErrorMessage ? (
          <div
            role="alert"
            data-slot="invite-dialog-error"
            className="text-sm text-status-danger-foreground"
          >
            {mutationErrorMessage}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            size={BUTTON_SIZE.DEFAULT}
            variant={BUTTON_VARIANT.SECONDARY}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite-member-form"
            variant={BUTTON_VARIANT.PRIMARY}
            size={BUTTON_SIZE.DEFAULT}
            isLoading={isInviteSubmitting}
            loadingLabel={loadingLabel}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
